import React from 'react';
import { cn } from 'cn';
import {
  Card as CardBase,
  CardContent,
  CardFooter as CardFooterBase,
  CardHeader as CardHeaderBase,
  CardTitle as CardTitleBase,
} from '@/components/ui/card';

type CardProps = React.ComponentPropsWithoutRef<'div'> & {
  /** O próprio card é o corpo (padding do `CardBody`), sem `CardBody` dentro. */
  body?: boolean;
};

/**
 * Card com borda, sombra e 24px de margem abaixo, no mesmo visual do `Panel`. Monte com `CardHeader`, `CardBody` e `CardFooter`. Para um card com título pronto, use o `Panel`; para um que abre e fecha, o `PanelCollapse`. Substitui o `Card` do reactstrap.
 * @import import { Card, CardBody, CardHeader } from 'src/componentes/card';
 */
export function Card({ body, className, ...props }: CardProps) {
  return (
    <CardBase
      className={cn('tw:mb-6', body && 'tw:flex-auto tw:p-4', className)}
      {...props}
    />
  );
}

/**
 * Cabeçalho do card.
 * @import import { CardHeader } from 'src/componentes/card';
 */
export function CardHeader(props: React.ComponentPropsWithoutRef<'div'>) {
  return <CardHeaderBase {...props} />;
}

/**
 * Corpo do card, com 16px de padding.
 * @import import { CardBody } from 'src/componentes/card';
 */
export function CardBody(props: React.ComponentPropsWithoutRef<'div'>) {
  return <CardContent {...props} />;
}

/**
 * Título do card.
 * @import import { CardTitle } from 'src/componentes/card';
 */
export function CardTitle(props: React.ComponentPropsWithoutRef<'div'>) {
  return <CardTitleBase {...props} />;
}

/**
 * Parágrafo de texto do card.
 * @import import { CardText } from 'src/componentes/card';
 */
export function CardText({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'p'>) {
  return <p className={cn('tw:last:mb-0', className)} {...props} />;
}

type CardImgProps = React.ComponentPropsWithoutRef<'img'> & {
  /** Imagem no topo do card, com os cantos de cima arredondados. */
  top?: boolean;
};

/**
 * Imagem do card.
 * @import import { CardImg } from 'src/componentes/card';
 */
export function CardImg({ top, className, ...props }: CardImgProps) {
  return (
    <img
      className={cn(
        'tw:h-auto tw:w-full tw:shrink-0',
        top ? 'tw:rounded-t-md' : 'tw:rounded-md',
        className
      )}
      {...props}
    />
  );
}

/**
 * Rodapé do card.
 * @import import { CardFooter } from 'src/componentes/card';
 */
export function CardFooter(props: React.ComponentPropsWithoutRef<'div'>) {
  return <CardFooterBase {...props} />;
}
